import React, { useEffect, useState } from 'react'
import { Table, Button, Modal, Input, Space, Select } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SlackOutlined,
} from '@ant-design/icons'
import axiosInstance from '../../Utils/axiosInstance'
import { toast } from 'react-toastify'

import {
  CreateProductRateDataAPI,
  DeleteProductRateDataAPI,
  GetProductDataAPI,
  GetProductRateDataAPI,
  UpdateProductRateDataAPI,
} from '../../AllAPI/AdminAPI'

const ProductRate = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))

  const [raws, setRaws] = useState([])
  const [products, setProducts] = useState([])
  const [searchText, setSearchText] = useState('')
  const [tableLoading, setTableLoading] = useState(false)
  const [productLoading, setProductLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  /* ================= Modal + Inputs ================= */
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [productId, setProductId] = useState(null)
  const [rate, setRate] = useState('')

  /* ================= Delete ================= */
  const [deleteId, setDeleteId] = useState(null)

  useEffect(() => {
    fetchProducts()
    fetchProductRateData()
  }, [])

  /* ================= Fetch Coloring Data ================= */
  const fetchProductRateData = async () => {
    try {
      setTableLoading(true)

      const res = await axiosInstance.get(
        `${GetProductRateDataAPI}/${selectedYear}`,
      )

      setRaws(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load data')
    } finally {
      setTableLoading(false)
    }
  }

  /* ================= Fetch Products (Dropdown) ================= */
  const fetchProducts = async () => {
    try {
      setProductLoading(true)

      const res = await axiosInstance.get(
        `${GetProductDataAPI}/${selectedYear}`,
      )

      setProducts(Array.isArray(res.data) ? res.data : [])
    } catch {
      toast.error('Failed to load products')
    } finally {
      setProductLoading(false)
    }
  }

  /* ================= Open Modal ================= */
  const openModal = (record = null) => {
    if (record) {
      setEditingId(record.id)
      setProductId(record.product.prod_id)
      setRate(record.rate)
    } else {
      setEditingId(null)
      setProductId(null)
      setRate('')
    }
    setIsModalOpen(true)
  }

  /* ================= Save / Update ================= */
  const handleSave = async () => {
    if (!productId) return toast.error('Product is required')
    if (!rate || Number(rate) <= 0) return toast.error('Enter valid rate')

    const objData = {
      prod_id: productId,
      rate: Number(rate),
      year: selectedYear,
    }

    try {
      setSaveLoading(true)

      let res

      if (editingId) {
        res = await axiosInstance.put(
          `${UpdateProductRateDataAPI}/${editingId}`,
          objData,
        )
      } else {
        res = await axiosInstance.post(CreateProductRateDataAPI, objData)
      }

      if (res.data === 'UPDATED') {
        toast.success('Product Rate Updated')
      } else if (res.data === 'SAVED') {
        toast.success('Product Rate Added')
      } else if (res.data === 'ALREADYEXIST') {
        toast.warn('Product Already Exist!')
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found')
      } else {
        toast.error('Error Occurred! Try Again')
      }

      fetchProductRateData()

      setIsModalOpen(false)
      setEditingId(null)
      setProductId(null)
      setRate('')
    } catch {
      toast.error('Failed to save product rate')
    } finally {
      setSaveLoading(false)
    }
  }

  /* ================= Delete ================= */

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true)

      const res = await axiosInstance.delete(
        `${DeleteProductRateDataAPI}/${deleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Product rate deleted successfully')
        setDeleteId(null)
        fetchProductRateData()
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found')
      }
    } catch {
      toast.error('Failed to delete product rate')
    } finally {
      setDeleteLoading(false)
    }
  }

  /* ================= Search ================= */
  const filteredRaws = raws.filter((r) => {
    const productName = r?.product?.product_name?.toLowerCase() || ''

    const search = searchText.toLowerCase()

    return productName.includes(search)
  })

  /* ================= Table ================= */
  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'product',
      render: (product) =>
        product ? `${product.product_name} (${product.size})` : '-',
    },
    {
      title: 'Rate',
      dataIndex: 'rate',
    },
    {
      title: 'Actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openModal(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            size="small"
            danger
            onClick={() => setDeleteId(record.id)}
          />
        </Space>
      ),
    },
  ]

  return (
    <>
      <div className="mainContainer">
        <div className="header-row">
          <h3>
            <SlackOutlined style={{ color: 'orangered' }} /> Product Rate
          </h3>
        </div>

        <div className="search-bar text-end">
          <Button
            className="addButton mx-md-2"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            Add
          </Button>

          <Input
            placeholder="Search by product..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 250 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredRaws}
          rowKey="id"
          loading={tableLoading}
          bordered
          className="shadow"
          style={{ overflowX: 'auto' }}
        />
      </div>

      {/* ================= MODAL ================= */}
      <Modal
        title={editingId ? 'Edit Product Rate' : 'Add Product Rate'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={saveLoading}
          >
            {editingId ? 'Update' : 'Save'}
          </Button>,
        ]}
      >
        <label>Product</label>
        <Select
          value={productId}
          onChange={setProductId}
          placeholder="Select product"
          style={{ width: '100%' }}
          loading={productLoading}
        >
          {products.map((p) => (
            <Select.Option key={p.prod_id} value={p.prod_id}>
              {p.product_name} &nbsp; ({p.size})
            </Select.Option>
          ))}
        </Select>

        <label className="mt-2">Rate</label>
        <Input
          type="number"
          min={1}
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          placeholder="Enter rate"
        />
      </Modal>

      {/* ================= DELETE ================= */}
      <Modal
        title="Confirm Delete"
        open={!!deleteId}
        onCancel={() => setDeleteId(null)}
        onOk={confirmDelete}
        confirmLoading={deleteLoading}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        Are you sure you want to delete this product rate?
      </Modal>
    </>
  )
}

export default ProductRate
